import { Stats } from "@src/domain/stats/value_objects/stats";
import type { PascoStats, DashboardStats, PascoJobStats } from "@src/domain/stats/value_objects/stats";
import type { StatsDTO } from "../dto";

/**
 * Stats DTO Mapper
 * Maps between domain Stats value object and application layer StatsDTO
 * 
 * This mapper ensures proper separation between domain and application layers,
 * following Clean Architecture principles.
 */
export class StatsDtoMapper {
    /**
     * Convert domain Stats value object to StatsDTO
     * @param stats - Domain Stats value object
     * @returns StatsDTO - Application layer DTO
     */
    static toDTO(stats: Stats): StatsDTO {
        return {
            pasco: {
                total_pascos: stats.pasco.totalPascos,
                total_question_chunks: stats.pasco.totalQuestionChunks,
                status_breakdown: stats.pasco.statusBreakdown,
                pending_approval: stats.pasco.pendingApproval,
                approved: stats.pasco.approved,
            },
            dashboard: {
                total_users: stats.dashboard.totalUsers,
                active_users: stats.dashboard.activeUsers,
                banned_users: stats.dashboard.bannedUsers,
                verified_users: stats.dashboard.verifiedUsers,
                users_with_roles: stats.dashboard.usersWithRoles,
                users_without_roles: stats.dashboard.usersWithoutRoles,
                total_roles: stats.dashboard.totalRoles,
                active_roles: stats.dashboard.activeRoles,
                total_permissions: stats.dashboard.totalPermissions,
            },
            pasco_jobs: {
                total_jobs: stats.pascoJobs.totalJobs,
                status_breakdown: stats.pascoJobs.statusBreakdown,
            },
            timestamp: stats.timestamp.toISOString(),
        };
    }

    /**
     * Convert StatsDTO to domain Stats value object
     * Useful for testing or when receiving data from presentation layer
     * @param dto - Application layer DTO
     * @returns Stats - Domain value object
     */
    static toDomain(dto: StatsDTO): Stats {
        const pasco: PascoStats = {
            totalPascos: dto.pasco.total_pascos,
            totalQuestionChunks: dto.pasco.total_question_chunks,
            statusBreakdown: dto.pasco.status_breakdown,
            pendingApproval: dto.pasco.pending_approval,
            approved: dto.pasco.approved,
        };

        const dashboard: DashboardStats = {
            totalUsers: dto.dashboard.total_users,
            activeUsers: dto.dashboard.active_users,
            bannedUsers: dto.dashboard.banned_users,
            verifiedUsers: dto.dashboard.verified_users,
            usersWithRoles: dto.dashboard.users_with_roles,
            usersWithoutRoles: dto.dashboard.users_without_roles,
            totalRoles: dto.dashboard.total_roles,
            activeRoles: dto.dashboard.active_roles,
            totalPermissions: dto.dashboard.total_permissions,
        };

        const pascoJobs: PascoJobStats = {
            totalJobs: dto.pasco_jobs.total_jobs,
            statusBreakdown: dto.pasco_jobs.status_breakdown,
        };

        return Stats.create({
            pasco,
            dashboard,
            pascoJobs,
            timestamp: new Date(dto.timestamp),
        });
    }
}
